import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Wallet, Pencil, Loader2, Plus, Trash2, AlertCircle, Lock } from 'lucide-react';
import { useCreatePayroll, usePayrollRecord, useUpdatePayroll } from '../../services/httpServices/queries';
import { EmployeeSearchSelect } from '../../components/ui/EmployeeSearchSelect';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { MonthPicker } from '../../components/ui/DatePicker';
import { employeeDisplayName, employeeInitials } from '../../utils/employee';
import type { Employee } from '../../services/httpServices/employeeService';
import type { HrEmployee } from '../../services/httpServices/hrService';

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-colors';
/** Read-only styling used for fields that are locked while editing. */
const lockedInputClass =
  'w-full px-3 py-2 border border-border rounded-lg bg-background/50 text-text-muted text-sm cursor-not-allowed';
const labelClass = 'block text-sm font-medium text-text mb-1';
const sectionTitleClass =
  'text-sm font-semibold text-text uppercase tracking-wide border-b border-border pb-2 mb-4';

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PROCESSED', label: 'Processed' },
  { value: 'FINALIZED', label: 'Finalized' },
  { value: 'PAID', label: 'Paid' },
];

const currentMonth = () => new Date().toISOString().slice(0, 7);

/** Turn a `YYYY-MM` period into inclusive ISO start/end dates. */
function monthBounds(period: string) {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month || month < 1 || month > 12) return null;
  const lastDay = new Date(year, month, 0).getDate();
  return {
    start: `${period}-01`,
    end: `${period}-${String(lastDay).padStart(2, '0')}`,
  };
}

const formatCurrency = (value: number) =>
  `৳${(Number.isFinite(value) ? value : 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

interface DeductionRow {
  id: string;
  reason: string;
  amount: string;
}

/** Read-only employee summary shown when the employee cannot be changed. */
function LockedEmployee({ employee, employeeId }: { employee?: HrEmployee | null; employeeId: string }) {
  const name = employee ? employeeDisplayName(employee) : employeeId.slice(0, 8) + '…';
  const meta = [employee?.employeeCode, employee?.designation?.name, employee?.department?.name]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className={`${lockedInputClass} flex items-center gap-3`}>
      <div className="w-8 h-8 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold overflow-hidden">
        {employee?.photoUrl ? (
          <img src={employee.photoUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          employeeInitials(employee as any)
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-text truncate">{name}</p>
        <p className="text-xs text-text-muted truncate">{meta || 'Employee'}</p>
      </div>
      <Lock size={14} className="ml-auto shrink-0 text-text-muted" />
    </div>
  );
}

export function PayrollForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const createPayroll = useCreatePayroll();
  const updatePayroll = useUpdatePayroll();
  const { data: recordData, isLoading: recordLoading, error: recordError } = usePayrollRecord(id || '');
  const record = isEdit ? recordData?.data : undefined;

  const [employeeId, setEmployeeId] = useState('');
  const [employee, setEmployee] = useState<Employee | undefined>(undefined);
  const [payPeriod, setPayPeriod] = useState(currentMonth());
  const [deductionItems, setDeductionItems] = useState<DeductionRow[]>([]);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('DRAFT');
  const [paymentReference, setPaymentReference] = useState('');
  const [error, setError] = useState('');
  const [initializedId, setInitializedId] = useState<string | null>(null);

  // Seed the form from the saved record once it loads (edit mode only).
  useEffect(() => {
    if (!isEdit || !record || initializedId === record.id) return;
    setEmployeeId(record.employeeId);
    setPayPeriod(record.payPeriod);
    setDeductionItems(
      (record.deductionItems || []).map((item) => ({
        id: crypto.randomUUID(),
        reason: item.reason || '',
        amount: item.amount !== undefined && item.amount !== null ? String(item.amount) : '',
      })),
    );
    setNotes(record.notes || '');
    setStatus(record.status || 'DRAFT');
    setPaymentReference(record.paymentReference || '');
    setInitializedId(record.id);
  }, [isEdit, record, initializedId]);

  const bounds = useMemo(() => monthBounds(payPeriod), [payPeriod]);

  // Compensation is read-only everywhere: from the employee profile when
  // creating, or from the saved payroll snapshot when editing.
  const compensation = useMemo(() => {
    if (isEdit && record) {
      return {
        basicSalary: Number(record.basicSalary) || 0,
        allowanceItems: record.allowanceItems || [],
        taxDeduction: Number(record.taxDeduction) || 0,
      };
    }
    return {
      basicSalary: Number(employee?.basicSalary) || 0,
      allowanceItems: employee?.allowanceItems || [],
      taxDeduction: Number(employee?.taxDeduction) || 0,
    };
  }, [isEdit, record, employee]);

  const { basicSalary, allowanceItems, taxDeduction } = compensation;
  const allowancesTotal = allowanceItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const grossSalary = basicSalary + allowancesTotal;
  const deductionsTotal = deductionItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const netSalary = grossSalary - deductionsTotal - taxDeduction;

  const handleEmployeeSelect = (selectedId: string, selected?: Employee) => {
    setEmployeeId(selectedId);
    setEmployee(selected);
    if (selectedId) setError('');
  };

  const addDeduction = () =>
    setDeductionItems((prev) => [...prev, { id: crypto.randomUUID(), reason: '', amount: '' }]);

  const updateDeduction = (rowId: string, field: 'reason' | 'amount', value: string) =>
    setDeductionItems((prev) => prev.map((item) => (item.id === rowId ? { ...item, [field]: value } : item)));

  const removeDeduction = (rowId: string) =>
    setDeductionItems((prev) => prev.filter((item) => item.id !== rowId));

  const cleanedDeductions = () =>
    deductionItems
      .map((item) => ({ reason: item.reason.trim(), amount: Number(item.amount) || 0 }))
      .filter((item) => item.reason || item.amount);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEdit && !employeeId) return setError('Please select an employee');
    if (!bounds) return setError('Please choose a valid pay period');
    setError('');

    if (isEdit && id) {
      updatePayroll.mutate(
        {
          id,
          data: {
            status,
            paymentReference: paymentReference.trim(),
            deductionItems: cleanedDeductions(),
            notes: notes.trim(),
          },
        },
        { onSuccess: () => navigate('/hr?tab=payroll') },
      );
      return;
    }

    createPayroll.mutate(
      {
        employeeId,
        payPeriod,
        periodStart: bounds.start,
        periodEnd: bounds.end,
        deductionItems: cleanedDeductions(),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      },
      { onSuccess: () => navigate('/hr?tab=payroll') },
    );
  };

  if (isEdit && recordLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isEdit && (recordError || !record)) {
    return (
      <div className="w-full space-y-4">
        <button
          onClick={() => navigate('/hr?tab=payroll')}
          className="inline-flex items-center gap-2 text-text-muted hover:text-text transition-colors text-sm cursor-pointer"
        >
          <ArrowLeft size={18} />
          Back to Payroll
        </button>
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load the payroll record. Please try again.</span>
        </div>
      </div>
    );
  }

  const submitting = createPayroll.isPending || updatePayroll.isPending;

  return (
    <div className="w-full space-y-4">
      <button
        onClick={() => navigate('/hr?tab=payroll')}
        className="inline-flex items-center gap-2 text-text-muted hover:text-text transition-colors text-sm cursor-pointer"
      >
        <ArrowLeft size={18} />
        Back to Payroll
      </button>

      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isEdit ? 'bg-primary/10' : 'bg-success/10'}`}>
          {isEdit ? <Pencil className="w-5 h-5 text-primary" /> : <Wallet className="w-5 h-5 text-success" />}
        </div>
        <div>
          <h1 className="text-2xl font-bold text-text">{isEdit ? 'Edit Payroll' : 'Process Payroll'}</h1>
          <p className="text-sm text-text-muted">
            {isEdit
              ? 'Employee, pay period and salary components are locked; deductions and payment details can be updated'
              : 'Salary, allowances and tax are pulled from the employee profile'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-danger/10 text-danger rounded-lg p-4 flex items-center gap-2 border border-danger/20">
          <AlertCircle size={18} />
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-surface border border-border rounded-xl p-5 space-y-6">
        <div>
          <p className={sectionTitleClass}>Employee &amp; Period</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              {isEdit ? (
                <>
                  <label className={labelClass}>Employee</label>
                  <LockedEmployee employee={record?.employee} employeeId={record?.employeeId || ''} />
                </>
              ) : (
                <EmployeeSearchSelect
                  value={employeeId}
                  onChange={handleEmployeeSelect}
                  label="Employee"
                  required
                  payrollOnly
                />
              )}
            </div>
            <div>
              <label className={labelClass}>
                Pay Period {!isEdit && <span className="text-danger">*</span>}
              </label>
              <MonthPicker
                required={!isEdit}
                value={payPeriod}
                onChange={setPayPeriod}
                disabled={isEdit}
                className={isEdit ? lockedInputClass : inputClass}
              />
              {bounds && (
                <p className="mt-1 text-xs text-text-muted">
                  {bounds.start} → {bounds.end}
                </p>
              )}
            </div>
          </div>
        </div>

        <div>
          <p className={sectionTitleClass}>Compensation (from employee)</p>
          {!isEdit && !employee ? (
            <p className="text-xs text-text-muted italic">
              Select an employee to load their salary, allowances and tax.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-lg border border-border bg-background/60 p-3">
                  <p className="text-xs text-text-muted">Gross Salary</p>
                  <p className="text-base font-semibold text-text">{formatCurrency(basicSalary)}</p>
                </div>
                <div className="rounded-lg border border-border bg-background/60 p-3">
                  <p className="text-xs text-text-muted">Tax</p>
                  <p className="text-base font-semibold text-text">{formatCurrency(taxDeduction)}</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-text-muted mb-2">Allowances</p>
                {allowanceItems.length === 0 ? (
                  <p className="text-xs text-text-muted italic">No allowances on the employee profile</p>
                ) : (
                  <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                    {allowanceItems.map((item, index) => (
                      <div key={index} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-text">{item.label || 'Allowance'}</span>
                        <span className="font-medium text-text">{formatCurrency(Number(item.amount) || 0)}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between px-3 py-2 text-sm bg-background/60">
                      <span className="text-text-muted">Total Allowances</span>
                      <span className="font-semibold text-text">{formatCurrency(allowancesTotal)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between border-b border-border pb-2 mb-4">
            <p className={`${sectionTitleClass} border-b-0 pb-0 mb-0`}>Deductions</p>
            <button
              type="button"
              onClick={addDeduction}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-danger border border-danger/30 rounded-lg hover:bg-danger/5 transition-colors"
            >
              <Plus size={13} /> Add Deduction
            </button>
          </div>
          {deductionItems.length === 0 ? (
            <p className="text-xs text-text-muted italic">No deductions added</p>
          ) : (
            <div className="space-y-2">
              {deductionItems.map((item) => (
                <div key={item.id} className="grid grid-cols-[1fr_8rem_auto] items-center gap-2">
                  <input
                    className={inputClass}
                    value={item.reason}
                    onChange={(e) => updateDeduction(item.id, 'reason', e.target.value)}
                    placeholder="Reason for deduction (e.g. Late arrival)"
                  />
                  <ValidatedNumberInput
                    className={inputClass}
                    value={item.amount}
                    onChange={(v) => updateDeduction(item.id, 'amount', String(v))}
                    placeholder="Amount"
                  />
                  <button
                    type="button"
                    onClick={() => removeDeduction(item.id)}
                    className="p-2 text-text-muted hover:text-danger hover:bg-danger/10 rounded-lg transition-colors"
                    title="Remove deduction"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-text-muted">
            Total deductions: <span className="font-medium text-text">{formatCurrency(deductionsTotal)}</span>
          </p>
        </div>

        {isEdit && (
          <div>
            <p className={sectionTitleClass}>Status &amp; Payment</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className={inputClass}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Payment Reference</label>
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="e.g. TXN-2026-0001"
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        )}

        <div>
          <label className={labelClass}>Notes</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional payment note"
            className={inputClass}
          />
        </div>

        <div className="rounded-lg border border-border bg-background/60 p-4 grid grid-cols-2 gap-2 text-sm">
          <span className="text-text-muted">Gross Pay</span>
          <span className="text-right font-medium text-text">{formatCurrency(grossSalary)}</span>
          <span className="text-text-muted">Total Deductions</span>
          <span className="text-right font-medium text-danger">
            {formatCurrency(deductionsTotal + taxDeduction)}
          </span>
          <span className="text-text-muted">Net Pay</span>
          <span className="text-right font-semibold text-success">{formatCurrency(netSalary)}</span>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-border">
          <button
            type="button"
            onClick={() => navigate('/hr?tab=payroll')}
            className="px-4 py-2 text-text-muted hover:text-text transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || (!isEdit && !employeeId)}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {isEdit ? 'Save Changes' : 'Process Payroll'}
          </button>
        </div>
      </form>
    </div>
  );
}
